/// <amd-module name="Repos/Discussion/Editor/Widgets/DiscussionZoneWidget" />
import * as React from "react";
import * as ReactDOM from "react-dom";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import {
    IDiffMonacoDiscussionWidget,
    IDiscussionThreadModel,
    ILineInfo,
    LineInfoType,
    SpecialCommentPositions
} from "Repos/Discussion/Common/DiscussionContracts";
import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { ReactRootComponent } from "VSS/Platform/Layout";
import { DiscussionBaseWidget, IDiscussionBaseWidgetProps, IDiscussionWidgetContentProps } from "./DiscussionBaseWidget";
import { createZoneWidget, IZoneWidget } from "./InternalZoneWidget";

export interface IDiscussionContentRenderProps {
    className?: string;
    onContentLoaded: (contentElement: HTMLElement | null) => void;
    threads: IReadonlyObservableArray<IDiscussionThreadModel>;
}

export interface IDiscussionZoneWidgetProps {
    contentClassName?: string;
    highlightSelection?: boolean;
    lineInfo?: ILineInfo;
    pageContext: IVssPageContext;
    renderContent: (props: IDiscussionContentRenderProps) => JSX.Element;
    renderOnLayout: boolean;
    threads: IReadonlyObservableArray<IDiscussionThreadModel>;
}

export class DiscussionZoneWidget {
    private editor: monaco.editor.ICodeEditor;
    private props: IDiscussionZoneWidgetProps;
    private lineHeight: number;
    private lineNumber = 1;
    private heightInLines: number | undefined = undefined;
    private widget: IZoneWidget;
    private heightObserver: IntersectionObserver | undefined;
    private contentElement: HTMLElement | undefined;
    private highlightDecorations: string[];
    private rendered: boolean;
    private pendingReveal: boolean;

    constructor(editor: monaco.editor.ICodeEditor, props: IDiscussionZoneWidgetProps) {
        this.editor = editor;
        this.props = props;
        this.lineHeight = this.editor.getOption(monaco.editor.EditorOption.lineHeight);
        this.lineNumber = props.lineInfo ? props.lineInfo.lineEnd : SpecialCommentPositions.File;

        this.addWidget();
    }

    public addWidget(): void {
        const { editor, props } = this;

        this.widget = createZoneWidget(editor, {
            onFillContainer: this.onFillContainer,
            onLayout: this.onLayout,
            onWidth: this.onWidth,
            isAccessible: true
        });

        this.widget.create();

        // First, show with height zero. This will trigger onLayout so that
        // we can measure content and set the height accordinbly
        this.widget.show(new monaco.Position(this.normalizeLineNumber(this.lineNumber), 0), 0);

        // Highlight the selected line
        const { highlightSelection, lineInfo } = props;
        if (highlightSelection && lineInfo) {
            this.highlightDecorations = editor.deltaDecorations(
                [],
                [
                    {
                        range: new monaco.Range(lineInfo.lineStart, lineInfo.lineStartOffset, lineInfo.lineEnd, lineInfo.lineEndOffset),
                        options: { inlineClassName: "highlight-discussion-range" }
                    }
                ]
            );
        }
    }

    public dispose(): void {
        if (this.heightObserver && this.contentElement) {
            this.heightObserver.unobserve(this.contentElement);
            delete this.heightObserver;
            delete this.contentElement;
        }

        ReactDOM.unmountComponentAtNode(this.widget.container);
        this.widget.dispose();

        this.editor.deltaDecorations(this.highlightDecorations || [], []);
        // @ts-ignore TypeScript 4.0 upgrade
        delete this.highlightDecorations;

        // @ts-ignore TypeScript 4.0 upgrade
        delete this.widget;
        // @ts-ignore TypeScript 4.0 upgrade
        delete this.editor;
        // @ts-ignore TypeScript 4.0 upgrade
        delete this.props;
        // @ts-ignore TypeScript 4.0 upgrade
        delete this.rendered;
    }

    public revealWidget(): void {
        if (this.heightInLines === undefined) {
            // If the height is uninitialized, wait. Scrolling now would be inaccurate, because other widgets
            // may be also pending layout, thus final scroll position may differ much.
            this.pendingReveal = true;
            return;
        }

        const { lineInfo } = this.props;

        // Have the editor show the highlighted lines + the widget.
        lineInfo && this.editor.revealLinesInCenter(lineInfo.lineStart,
            lineInfo.lineEnd === SpecialCommentPositions.File ? lineInfo.lineEnd : lineInfo.lineEnd + this.heightInLines);
        const textFields = this.widget.container.getElementsByClassName("bolt-textfield-input");
        if (textFields.length > 0) {
            (textFields[0] as HTMLElement).focus();
        }
    }

    private normalizeLineNumber(lineNumber: number): number {
        const { lineInfo } = this.props;
        const showAbove = !lineInfo || lineInfo.lineEnd === SpecialCommentPositions.File;
        return lineNumber + (showAbove ? 1 : 0);
    }

    private onFillContainer = (container: HTMLElement): void => {
        if (!this.props.renderOnLayout) {
            this.render(container);
        }
    };

    private render(container: HTMLElement): void {
        ReactDOM.render(
            <ReactRootComponent pageContext={this.props.pageContext}>
                {this.props.renderContent({
                    className: this.props.contentClassName,
                    onContentLoaded: this.onContentLoaded,
                    threads: this.props.threads
                })}
            </ReactRootComponent>,
            container
        );

        this.rendered = true;
    }

    private onContentLoaded = (contentElement: HTMLElement | null): void => {
        if (contentElement) {
            this.contentElement = contentElement;
            this.observeHeightChange(contentElement);
        }
    };

    private onLayout = (height: number): void => {
        const { props, widget } = this;
        const { renderOnLayout } = props;

        // renderOnLayout makes sure that the widget is rendered when the container
        // is attached to the DOM tree. This is necessary for necessary measurements
        // to be performed properly
        if (!this.rendered && renderOnLayout && widget.domNode.parentNode) {
            this.render(widget.container);
        }

        // Make sure the container element is smaller than the content element so that we can
        // get called when the content shrinks.
        // If we don't do that, content height changes but since the treshold value is still 1,
        // intersection callback is not invoked.
        widget.domNode.style.height = Math.max(height - this.lineHeight * 2, 0) + "px";

        // This is required to show expand discussion thread button next to the
        // associated line number outside of widget.
        widget.container.style.overflow = "visible";

        this.onWidth();
    };

    private onWidth = (): void => {
        // Decrease given width (all-wide) as it would cover other previous widgets (findWidget)
        // Size is 744px max-width + 70px margin-left
        this.widget.domNode.style.maxWidth = "814px";
    };

    private observeHeightChange(contentElement: HTMLElement): void {
        const intersectionOptions: IntersectionObserverInit = {
            root: this.widget.domNode,
            threshold: []
        };

        // Make sure we have enough tresholds to cover large height changes
        for (let i = 0; i <= 100; i++) {
            (intersectionOptions.threshold as number[]).push(i / 100);
        }

        const intersectionCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
            entries.forEach(entry => {
                // Update height according to new content height
                this.updateHeight(entry.boundingClientRect.height);
            });
        };

        this.heightObserver = new IntersectionObserver(intersectionCallback, intersectionOptions);
        this.heightObserver.observe(contentElement);
    }

    private updateHeight(height: number): void {
        // Avoid rounding issues in Chrome (e.g. 128px becomes 128.015). Don't take 1 line for 0.015px
        const previousHeight = this.heightInLines || 0;
        this.heightInLines = height < 1 ? 0 : Math.ceil(height / this.lineHeight);

        if (previousHeight !== this.heightInLines) {
            // If we have new height, apply it and keep a reference
            this.widget._relayout(this.heightInLines);
        } else {
            // We only scroll after height is stable. We rely on a 2nd call that's triggered by _relayout.
            // If we scroll before, widgets are rendered overlapping lines, so scrolling is not accurate yet.
            if (this.pendingReveal) {
                this.pendingReveal = false;
                this.revealWidget();
            }
        }
    }
}

export interface IDiscussionZoneWidgetRegistrationProps extends IDiscussionBaseWidgetProps<IDiscussionWidgetContentProps> {
    diffOrientation: DiffViewerOrientation;
    scrollToThreadId: number | undefined;
}

let widgetId = 1;

export class DiscussionZoneWidgetRegistration
    extends DiscussionBaseWidget<IDiscussionZoneWidgetRegistrationProps>
    implements IDiffMonacoDiscussionWidget
{
    private editor: monaco.editor.ICodeEditor | undefined;
    private diffEditor: monaco.editor.IDiffEditor | undefined;
    private widget: DiscussionZoneWidget | undefined;
    private hasToBringIntoView = false;

    public readonly addToOriginalDiff: boolean | undefined;
    public diffOrientation: DiffViewerOrientation;
    public readonly widgetId: string;

    constructor(props: IDiscussionZoneWidgetRegistrationProps) {
        super(props);
        this.addToOriginalDiff = props.lineInfo?.lineStart === -1 ? undefined : props.addToOriginalDiff;
        this.diffOrientation = props.diffOrientation;
        this.widgetId = `discussion-zone-widget-${widgetId++}`;
    }

    public addWidget(editor: monaco.editor.ICodeEditor, diffEditor?: monaco.editor.IDiffEditor): DiscussionZoneWidget | undefined {
        if (diffEditor) {
            const isOriginalDiff = diffEditor.getOriginalEditor() === editor;
            if (isOriginalDiff !== this.props.addToOriginalDiff) {
                return undefined;
            }
        }

        this.editor = editor;
        this.diffEditor = diffEditor;
        this.widget = this.createWidget();

        if (this.hasToBringIntoView) {
            this.widget.revealWidget();
            this.hasToBringIntoView = false;
        }

        return this.widget;
    }

    public addThread(thread: IDiscussionThreadModel): void {
        super.addThread(thread);

        if (this.props.scrollToThreadId) {
            if (thread.id === this.props.scrollToThreadId) {
                this.revealWidget();
            }
        } else {
            if (thread.isNew()) {
                this.revealWidget();
            }
        }
    }

    public removeWidget() {
        this.widget?.dispose();

        delete this.diffEditor;
        delete this.editor;
        delete this.widget;
    }

    public changeOrientation(diffOrientation: DiffViewerOrientation): void {
        this.widget?.dispose();
        this.diffOrientation = diffOrientation;
        this.widget = this.createWidget();
    }

    public revealWidget(): void {
        if (this.widget) {
            this.widget.revealWidget();
        } else {
            this.hasToBringIntoView = true;
        }
    }

    private createWidget(): DiscussionZoneWidget {
        const { addToOriginalDiff, contentClassName, pageContext, renderContent } = this.props;

        // For inline diff viewer, we add the widget to modified side if a widget is meant
        // to be added to the original side. Since there is no space to show widget for the
        // original editor, it just shows the line numbers
        const addToOppositeEditor = this.diffOrientation === DiffViewerOrientation.Inline;

        let lineInfo = this.props.lineInfo;
        let widgetEditor = this.editor;
        let highlightSelection = true;
        const diffEditor = this.diffEditor;

        if (addToOriginalDiff && addToOppositeEditor && diffEditor && lineInfo) {
            lineInfo = {
                lineEnd: diffEditor.getDiffLineInformationForOriginal(lineInfo.lineEnd)!.equivalentLineNumber,
                lineEndOffset: lineInfo.lineEndOffset,
                lineStart: diffEditor.getDiffLineInformationForOriginal(lineInfo.lineStart)!.equivalentLineNumber,
                lineStartOffset: lineInfo.lineStartOffset,
                type: LineInfoType.Actual
            };

            widgetEditor = diffEditor.getModifiedEditor();
            highlightSelection = false;
        }

        // CodeEditor adds the widgets during the lifecycle of React where all the renders are batched and widget
        // content is rendered after widget container is added to the DOM tree.
        //
        // However, we need to wait for diff computation finished for DiffEditor and then add the widgets where
        // React doesn't batch the render operation and performs rendering widget content immediately which ends up
        // content render happening before widget container is added to the DOM tree. This causes invalid
        // measurements in core components.

        // `renderOnLayout` makes sure that the widget content is rendered when widget container is in the DOM tree.
        const renderOnLayout = !!diffEditor;

        const widget = new DiscussionZoneWidget(widgetEditor!, {
            contentClassName,
            highlightSelection,
            lineInfo,
            pageContext,
            renderContent,
            renderOnLayout,
            threads: this.threads
        });

        return widget;
    }
}
